import { useEffect, useRef, useState } from 'react'

// Debounces a value - delays updates until after delayMS of inactivity
export function useDebounce<T>(value: T, delayMS: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delayMS)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delayMS])

  return debouncedValue
}

// Debounces a function - delays execution until after delayMS of inactivity
export function useDebouncedCallback<T extends(...args: unknown[]) => unknown>(callback: T, delayMS: number): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const debouncedCallback = ((...args: Parameters<T>) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout
    const newTimeoutId = setTimeout(() => {
      callbackRef.current(...args)
    }, delayMS)

    timeoutRef.current = newTimeoutId
  }) as T

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedCallback
}
